import React, { useState, useEffect } from "react";
import "./FileUpload.scss";
import defaultImageIcon from "../../../assets/icons/grafico_ponerr_imagen.svg";

/**
 * FileUpload Component
 *
 * @param {Object} props
 * @param {string} props.variant - "card" o "button". Por defecto "card"
 * @param {string} props.accept - Tipos de archivo aceptados (ej: "image/*", ".pdf,.doc,.docx", etc.)
 * @param {function} props.onChange - Callback que recibe el archivo seleccionado
 * @param {string} props.initialFile - URL o base64 del archivo inicial
 * @param {string} props.label - Texto del label o botón
 * @param {number} props.maxSize - Tamaño máximo en MB. Por defecto 5MB
 * @param {string} props.className - Clases CSS adicionales
 *
 * Ejemplos:
 * <FileUpload variant="card" accept="image/*" onChange={handleImageChange} />
 * <FileUpload variant="button" accept=".pdf,.doc,.docx" label="Subir Documento" />
 */

const FileUpload = ({
    variant = "card",
    accept = "*/*",
    onChange,
    initialFile = null,
    label = "Seleccionar archivo",
    maxSize = 5, // MB
    className = "",
}) => {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(initialFile);
    const [fileName, setFileName] = useState("");
    const [fileType, setFileType] = useState("");
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [error, setError] = useState("");

    // Generar ID único para el input
    const inputId = `file-upload-${Math.random().toString(36).substr(2, 9)}`;

    // Detectar si initialFile es un PDF basándose en la extensión
    const isPdfUrl = (url) => {
        if (!url) return false;
        return (
            url.toLowerCase().endsWith(".pdf") ||
            url.toLowerCase().includes(".pdf?")
        );
    };

    // Extraer nombre de archivo de una URL
    const getFileNameFromUrl = (url) => {
        if (!url) return "";
        try {
            const urlObj = new URL(url);
            const pathname = urlObj.pathname;
            const fileName = pathname.substring(pathname.lastIndexOf("/") + 1);
            return decodeURIComponent(fileName);
        } catch {
            // Si no es una URL válida, intentar obtener el último segmento
            const segments = url.split("/");
            return segments[segments.length - 1] || "archivo";
        }
    };

    // Sincronizar preview cuando cambia initialFile
    useEffect(() => {
        if (initialFile) {
            setPreview(initialFile);

            // Si es un PDF, establecer el nombre y tipo del archivo
            if (isPdfUrl(initialFile)) {
                setFileName(getFileNameFromUrl(initialFile));
                setFileType("application/pdf");
            } else {
                // Para imágenes u otros archivos
                setFileName(getFileNameFromUrl(initialFile));
            }
        }
    }, [initialFile]);

    const getFileIcon = (type) => {
        if (type.startsWith("image/")) return "pi pi-image";
        if (type === "application/pdf") return "pi pi-file-pdf";
        if (type.includes("word") || type.includes("document"))
            return "pi pi-file-word";
        if (type.includes("excel") || type.includes("spreadsheet"))
            return "pi pi-file-excel";
        if (type.includes("zip") || type.includes("compressed"))
            return "pi pi-file-archive";
        return "pi pi-file";
    };

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (!selectedFile) return;

        // Validar tamaño
        const fileSizeInMB = selectedFile.size / (1024 * 1024);
        if (fileSizeInMB > maxSize) {
            setError(`El archivo excede el tamaño máximo de ${maxSize}MB`);
            return;
        }

        setError("");
        setFile(selectedFile);
        setFileName(selectedFile.name);
        setFileType(selectedFile.type);

        // Generar preview para imágenes
        if (selectedFile.type.startsWith("image/")) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setPreview(e.target.result);
            };
            reader.readAsDataURL(selectedFile);
        } else {
            setPreview(null);
        }

        // Callback al padre
        if (onChange) {
            onChange(selectedFile);
        }
    };

    const handleClick = () => {
        document.getElementById(inputId).click();
    };

    const handlePreviewClick = () => {
        if (variant === "button" && (preview || file)) {
            setShowPreviewModal(true);
        }
    };

    const renderPreviewContent = () => {
        // Si es un PDF (tanto archivo nuevo como URL existente)
        if (fileType === "application/pdf" || isPdfUrl(preview)) {
            const pdfUrl = file ? URL.createObjectURL(file) : preview;
            return (
                <div className="file-upload__pdf-preview">
                    <iframe
                        src={pdfUrl}
                        title="PDF Preview"
                        className="file-upload__pdf-frame"
                    />
                    <div className="file-upload__pdf-actions">
                        <a
                            href={pdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="file-upload__pdf-link"
                        >
                            <i className="pi pi-external-link"></i>
                            Abrir en nueva pestaña
                        </a>
                    </div>
                </div>
            );
        }

        // Si es una imagen con preview
        if (preview && !isPdfUrl(preview)) {
            return (
                <img
                    src={preview}
                    alt="Preview"
                    className="file-upload__preview-image"
                />
            );
        }

        // Si es otro tipo de archivo
        if (file) {
            return (
                <div className="file-upload__file-info">
                    <i
                        className={`${getFileIcon(
                            fileType,
                        )} file-upload__file-icon`}
                    ></i>
                    <p className="file-upload__file-name">{fileName}</p>
                    <p className="file-upload__file-type">
                        {fileType.split("/")[1]?.toUpperCase() || "FILE"}
                    </p>
                </div>
            );
        }

        return null;
    };

    // Variante CARD (cuadro clickeable)
    if (variant === "card") {
        return (
            <div className={`file-upload file-upload--card ${className}`}>
                <input
                    type="file"
                    id={inputId}
                    accept={accept}
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                />
                <div
                    className="file-upload__card-wrapper"
                    onClick={handleClick}
                >
                    {preview && !isPdfUrl(preview) ? (
                        <img
                            src={preview}
                            alt="Uploaded"
                            className="file-upload__card-image"
                        />
                    ) : file || (preview && isPdfUrl(preview)) ? (
                        <div className="file-upload__card-file">
                            <i
                                className={`${getFileIcon(
                                    fileType,
                                )} file-upload__card-icon`}
                            ></i>
                            <p className="file-upload__card-filename">
                                {fileName}
                            </p>
                        </div>
                    ) : (
                        <div className="file-upload__card-placeholder">
                            <img src={defaultImageIcon} alt="Upload" />
                            <p>{label}</p>
                        </div>
                    )}
                </div>
                {error && <p className="file-upload__error">{error}</p>}
            </div>
        );
    }

    // Variante BUTTON (botón con preview modal)
    return (
        <div className={`file-upload file-upload--button ${className}`}>
            <input
                type="file"
                id={inputId}
                accept={accept}
                onChange={handleFileChange}
                style={{ display: "none" }}
            />

            <div className="file-upload__button-group">
                <button
                    type="button"
                    className="file-upload__button-select"
                    onClick={handleClick}
                >
                    <i className="pi pi-upload"></i>
                    <span>{label}</span>
                </button>

                {(fileName || file) && (
                    <button
                        type="button"
                        className="file-upload__button-preview"
                        onClick={handlePreviewClick}
                    >
                        <i className={getFileIcon(fileType)}></i>
                        <span>{fileName}</span>
                        <i className="pi pi-eye"></i>
                    </button>
                )}
            </div>

            {error && <p className="file-upload__error">{error}</p>}

            {/* Modal de Preview Personalizado */}
            {showPreviewModal && (
                <div
                    className="file-upload__modal-overlay"
                    onClick={() => setShowPreviewModal(false)}
                >
                    <div
                        className="file-upload__modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header del Modal */}
                        <div className="file-upload__modal-header">
                            <h3 className="file-upload__modal-title">
                                Vista Previa del Archivo
                            </h3>
                            <button
                                className="file-upload__modal-close"
                                onClick={() => setShowPreviewModal(false)}
                                aria-label="Cerrar"
                            >
                                <i className="pi pi-times"></i>
                            </button>
                        </div>

                        {/* Body del Modal */}
                        <div className="file-upload__modal-body">
                            {renderPreviewContent()}

                            {!preview && !file && (
                                <p className="file-upload__no-preview">
                                    No hay vista previa disponible
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FileUpload;
